import { EmptyListNotice } from "@hatch-digital-nl/abs-storybook/legacy";
import {
    useGetCertificatesQuery,
    usePatchCertificateMutation,
} from "../../../common/api/certificate";
import Skeleton from "react-loading-skeleton";
import {
    setCurrentPage,
    setIsLeftTagModalOpenIndex,
    setIsReviewCertificateSuccess,
    setIsRightTagModalOpenIndex,
} from "../ReviewSlice";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useCallback, useEffect, useRef, JSX } from "react";
import { useEstablishmentId } from "../../../common/utils/useEstablishmentId";
import { CertificateStatusEnum } from "../../../common/enums/CertificateStatusEnum";
import { ReviewPagination } from "./ReviewPagination";
import { ReviewCard } from "./ReviewCard";
import { RejectCertificateModal } from "../../../modals/RejectCertificateModal/RejectCertificateModal";

export const ReviewContent = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const tagRef = useRef<HTMLDivElement>(null);
    const { currentPage, itemsPerPage, itemsPerPageSelect, searchTerm } =
        useAppSelector((state) => state.review);
    const { isPatchEditCertificateSuccess } = useAppSelector(
        (state) => state.editCertificateModal,
    );
    const { isPostCreateCertificateSuccess } = useAppSelector(
        (state) => state.createCertificateModal,
    );
    const {
        data: certificates,
        isLoading,
        isFetching,
        refetch,
    } = useGetCertificatesQuery({
        page: currentPage,
        items_per_page: itemsPerPage,
        filter: {
            establishment_id: useEstablishmentId(),
            status: CertificateStatusEnum.PENDING,
            search: searchTerm,
        },
    });
    const [patchCertificate, { isSuccess: isPatchCertificateSuccess }] =
        usePatchCertificateMutation();

    useEffect(() => {
        if (isPostCreateCertificateSuccess || isPatchEditCertificateSuccess) {
            refetch();
        }
    }, [
        isPostCreateCertificateSuccess,
        isPatchEditCertificateSuccess,
        refetch,
    ]);

    useEffect(() => {
        if (certificates && certificates.data.length === 0 && currentPage > 1) {
            dispatch(setCurrentPage(1));
        }
    }, [certificates, currentPage, dispatch]);

    useEffect(() => {
        if (!isPatchCertificateSuccess) {
            dispatch(setIsReviewCertificateSuccess(false));
        }
    }, [isPatchCertificateSuccess, dispatch]);

    const handleClickOutside = useCallback(
        (event: MouseEvent): void => {
            if (
                tagRef.current &&
                !tagRef.current.contains(event.target as Node)
            ) {
                dispatch(setIsLeftTagModalOpenIndex(-1));
                dispatch(setIsRightTagModalOpenIndex(-1));
            }
        },
        [dispatch, tagRef],
    );

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    if (isLoading || isFetching) {
        return (
            <Skeleton count={5} height={"16.7rem"} borderRadius={"1.5rem"} />
        );
    }

    if (!certificates || !certificates.data.length) {
        return (
            <EmptyListNotice
                content={"Er zijn geen items gevonden om te beoordelen"}
            />
        );
    }

    return (
        <>
            <div className={"p-review__content"}>
                {certificates?.data.map((certificate) => {
                    return (
                        <ReviewCard
                            key={certificate.id}
                            certificate={certificate}
                            patchCertificate={patchCertificate}
                            refetch={refetch}
                            tagRef={tagRef}
                        />
                    );
                })}
            </div>
            <ReviewPagination
                itemsPerPageSelect={itemsPerPageSelect}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                certificates={certificates}
            />
            <RejectCertificateModal
                refetch={refetch}
                patchCertificate={patchCertificate}
            />
        </>
    );
};
